import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const DATE_VIEWS = ['scheduled'];
const SPECIAL_VIEWS = ['all', 'starred', ...DATE_VIEWS];
const SELECTION_STORAGE_KEY = 'sidebarSelection';

function loadStoredSelection() {
    try {
        const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
    } catch {
        return null;
    }
}

export {SPECIAL_VIEWS};

export default function useChecklistViews({checklistData, groups, initialGroups, onViewLabelChange}) {
    const stored = loadStoredSelection();
    const [selectedGroupId, setSelectedGroupId] = useState(stored ? (stored.selectedGroupId ?? null) : null);
    const [focusedId, setFocusedId] = useState(null);
    const [innerView, setInnerView] = useState(stored?.innerView === 'templates' ? 'templates' : 'checklists');
    const [viewModes, setViewModes] = useState(() => {
        try {
            const raw = localStorage.getItem('viewModes');
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    });

    const viewModeKey =
        innerView === 'templates'
            ? 'templates'
            : SPECIAL_VIEWS.includes(selectedGroupId)
              ? `view:${selectedGroupId}`
              : `group:${selectedGroupId ?? 'inbox'}`;
    const viewMode = viewModes[viewModeKey] || (innerView === 'templates' ? 'grid' : 'columns');

    const toggleViewMode = useCallback(
        mode => {
            setViewModes(prev => {
                const next = {...prev, [viewModeKey]: mode};
                localStorage.setItem('viewModes', JSON.stringify(next));
                return next;
            });
        },
        [viewModeKey],
    );

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = e => {
            if (e.key === 'Escape') setFocusedId(null);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    const focusedChecklist = useMemo(
        () => (focusedId ? checklistData.find(c => c.id === focusedId) : null),
        [focusedId, checklistData],
    );

    const focusChecklist = useCallback(id => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    const isReadOnlyView = selectedGroupId === 'starred' || DATE_VIEWS.includes(selectedGroupId);
    const isReorderable = !isReadOnlyView && selectedGroupId !== 'all';

    const visibleChecklists = useMemo(() => {
        if (selectedGroupId === 'all') return checklistData;
        if (selectedGroupId === 'starred') {
            return checklistData
                .filter(c => c.items.some(i => i.starred))
                .map(c => ({...c, items: c.items.filter(i => i.starred)}));
        }
        if (selectedGroupId === 'scheduled') return checklistData;
        if (selectedGroupId === null) return checklistData.filter(c => c.groupId == null);
        return checklistData.filter(c => c.groupId === selectedGroupId);
    }, [checklistData, selectedGroupId]);

    const selectGroup = useCallback(id => {
        setFocusedId(null);
        setInnerView('checklists');
        setSelectedGroupId(id);
    }, []);
    const showTemplates = useCallback(() => {
        setFocusedId(null);
        setInnerView('templates');
        setSelectedGroupId(undefined);
    }, []);

    const handleDeletedGroup = useCallback(id => {
        setSelectedGroupId(prev => (prev === id ? 'all' : prev));
    }, []);

    const handleChecklistCreated = useCallback(created => {
        setFocusedId(created.id);
    }, []);

    const handleSpawn = useCallback(checklist => {
        setInnerView('checklists');
        setSelectedGroupId(checklist.groupId || null);
    }, []);

    const handleDeletedChecklist = useCallback(id => {
        setFocusedId(prev => (prev === id ? null : prev));
    }, []);

    useEffect(() => {
        if (innerView === 'templates') {
            onViewLabelChange('Templates');
            return;
        }
        if (selectedGroupId === 'all') {
            onViewLabelChange('All');
            return;
        }
        if (selectedGroupId === 'starred') {
            onViewLabelChange('Starred');
            return;
        }
        if (selectedGroupId === 'scheduled') {
            onViewLabelChange('Scheduled');
            return;
        }
        if (selectedGroupId === null) {
            onViewLabelChange(groups.length > 0 ? 'Inbox' : 'All');
            return;
        }
        const group = groups.find(g => g.id === selectedGroupId);
        onViewLabelChange(group ? group.name : 'All');
    }, [selectedGroupId, groups, innerView, onViewLabelChange]);

    useEffect(() => {
        localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify({selectedGroupId, innerView}));
    }, [selectedGroupId, innerView]);

    const hasValidatedStoredSelectionRef = useRef(false);
    useEffect(() => {
        if (hasValidatedStoredSelectionRef.current) return;
        if (initialGroups == null) return;
        hasValidatedStoredSelectionRef.current = true;
        if (innerView !== 'checklists') return;
        if (selectedGroupId == null || SPECIAL_VIEWS.includes(selectedGroupId)) return;
        if (!initialGroups.some(g => g.id === selectedGroupId)) {
            setSelectedGroupId('all');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialGroups]);

    return {
        selectedGroupId,
        focusedId,
        focusedChecklist,
        innerView,
        viewMode,
        toggleViewMode,
        visibleChecklists,
        isReadOnlyView,
        isReorderable,
        focusChecklist,
        unfocusChecklist,
        selectGroup,
        showTemplates,
        handleDeletedGroup,
        handleChecklistCreated,
        handleSpawn,
        handleDeletedChecklist,
    };
}
